import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { AddBlock } from '@lyearn/molecules';
import { Box, Stack, Typography } from '@lyearn/ui';
import useListWithTempId from '@/hooks/useList/useListWithTempId';
import { ExerciseQuestionInput } from '@/types/schema';

import { validateExerciseQuestion } from '../Editor/validations';
import { DEFAULT_QUESTION_ITEM } from './consts';
import Question from './Question';
import { QuestionsProps } from './types';
import { getSubmissionFormats, isQuestionsEmpty } from './utils';

function Questions(props: QuestionsProps) {
  const { error, questions, onChange, setErrorElement } = props;
  const { t } = useTranslation();
  const draggingItem = useRef<number | undefined>();
  const responseFormats = useMemo(() => getSubmissionFormats(t), [t]);
  const [questionList, actions] = useListWithTempId<ExerciseQuestionInput>(questions, onChange);
  const { push, onDelete, onDuplicate, onMove, onListInputChange } = actions;
  const questionRefs = useRef<Record<string, HTMLElement | null>>({});

  const onChangeQuestion = useCallback(
    (index: number, updatedQuestion: ExerciseQuestionInput) => {
      onListInputChange(updatedQuestion, index);
    },
    [onListInputChange],
  );

  useEffect(() => {
    const errorItem = questionList.find((question) => validateExerciseQuestion(t, question));

    if (errorItem) {
      setErrorElement(questionRefs.current[errorItem.tempId]);
    } else {
      setErrorElement(null);
    }
  }, [questionList, setErrorElement, t]);

  const onDrop = useCallback(
    (index: number) => {
      const draggedItemIndex = draggingItem.current;
      if (draggedItemIndex !== undefined) {
        onMove(draggedItemIndex, index);
      }
    },
    [onMove],
  );

  return (
    <Stack className="mt-8" direction="col" gapY={24}>
      {questionList.map((question, index) => (
        <Box key={question.tempId} ref={(el) => (questionRefs.current[question.tempId] = el)}>
          <Question
            key={question.tempId}
            draggingItem={draggingItem}
            error={error ? validateExerciseQuestion(t, question)?.message : undefined}
            index={index}
            item={question}
            responseFormats={responseFormats}
            onChangeQuestion={onChangeQuestion}
            onDelete={onDelete}
            onDrop={onDrop}
            onDuplicate={onDuplicate}
          />
        </Box>
      ))}
      <Box>
        <AddBlock
          title={t('Exercise:Creator.AddQuestion')}
          onClick={() => push(DEFAULT_QUESTION_ITEM)}
        />
        {error && isQuestionsEmpty(questions) ? (
          <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
            {error}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export default React.memo(Questions);
